import type { SupportModeSession } from "@/context/supportMode/supportModeSession";
import {
  readRememberedAccounts,
  type AccountPair,
} from "@/store/rememberedAccountsStorage";
import { isCdhIdIamIdMigrationEnabled } from "@/store/dxpIamAuthHeader";
import { create } from "zustand";

/**
 * Dealer URLs carry the IAM account id, while API scoping (`dxp-account-ids`,
 * GraphQL variables) still uses the CDH id, so the route id has to be mapped back.
 */
type DealerAccountIdsStore = {
  cdhIdByIamId: ReadonlyMap<string, string>;
  addDealerAccountIds: (pairs: readonly AccountPair[]) => void;
};

const readRememberedCdhIdByIamId = (): ReadonlyMap<string, string> => {
  const remembered = readRememberedAccounts();
  return new Map(
    [remembered?.primary, remembered?.last].flatMap(
      (pair): [string, string][] =>
        pair === undefined ? [] : [[pair.iamId, pair.cdhId]]
    )
  );
};

export const useDealerAccountIdsStore = create<DealerAccountIdsStore>(
  (set) => ({
    cdhIdByIamId: readRememberedCdhIdByIamId(),
    addDealerAccountIds: (pairs) => {
      set((state) => {
        const hasChanges = pairs.some(
          ({ cdhId, iamId }) => state.cdhIdByIamId.get(iamId) !== cdhId
        );
        if (!hasChanges) {
          return state;
        }

        const cdhIdByIamId = new Map(state.cdhIdByIamId);
        for (const { cdhId, iamId } of pairs) {
          cdhIdByIamId.set(iamId, cdhId);
        }
        return { cdhIdByIamId };
      });
    },
  })
);

export const resetDealerAccountIdsStore = (): void => {
  useDealerAccountIdsStore.setState({
    cdhIdByIamId: readRememberedCdhIdByIamId(),
  });
};

export const resolveDealerCdhAccountId = (
  routeIamId: string | undefined,
  cdhIdByIamId: ReadonlyMap<string, string>,
  supportModeSession: SupportModeSession | null
): string | undefined => {
  if (routeIamId === undefined || routeIamId.length === 0) {
    return undefined;
  }

  if (!isCdhIdIamIdMigrationEnabled()) {
    const cdhIdFromIamSegment = cdhIdByIamId.get(routeIamId);
    return cdhIdFromIamSegment ?? routeIamId;
  }

  if (supportModeSession?.iamAccountId === routeIamId) {
    return supportModeSession.cdhId;
  }

  return cdhIdByIamId.get(routeIamId);
};
